import {
  failAuthJourney,
  startAuthJourney,
} from "@src/features/auth/analytics/authJourneyLease"
import {
  classifyPasskeyLoginFailure,
  isActionablePasskeyFailure,
  type PasskeyLoginFailureType,
} from "@src/features/webauthn/lib/classifyPasskeyLoginFailure"
import { useLastConnectedWalletStore } from "@src/stores/useLastConnectedWalletStore"
import { toError } from "@src/utils/errors"
import { logger } from "@src/utils/logger"
import { create } from "zustand"
import { useWebAuthnStore } from "./useWebAuthnStore"

const SIGN_IN_CREDENTIAL_NOT_FOUND_MESSAGE =
  "This passkey isn’t registered with this app. Create a new passkey below or use another sign-in method."
const SIGN_IN_CANCELLED_HINT = "Cancelled or timed out. Please try again."

function getPasskeyLoginErrorMessage(
  failureType: PasskeyLoginFailureType,
  rawMessage: string
): string | null {
  if (failureType === "cancelled") {
    return null
  }

  if (failureType === "credential_not_found") {
    return SIGN_IN_CREDENTIAL_NOT_FOUND_MESSAGE
  }

  return rawMessage
}

type Mode = "choose" | "create"

type State = {
  isOpen: boolean
  // In the store so the view survives closing the dialog during the ceremony.
  mode: Mode
  isCreating: boolean
  isSigningIn: boolean
  createError: string | null
  signInError: string | null
  signInHint: string | null
}

type Actions = {
  open: () => void
  close: () => void
  setMode: (mode: Mode) => void
  createNew: (passkeyName: string) => Promise<void>
  signIn: () => Promise<void>
  clearCreateError: () => void
  clearSignInError: () => void
}

export const useWebAuthnUIStore = create<State & Actions>()((set, get) => ({
  isOpen: false,
  mode: "choose",
  isCreating: false,
  isSigningIn: false,
  createError: null,
  signInError: null,
  signInHint: null,

  open: () => {
    // Ignore re-entry while a ceremony runs, so its prompt can't be covered again.
    if (get().isCreating || get().isSigningIn) return
    set({
      isOpen: true,
      mode: "choose",
      createError: null,
      signInError: null,
      signInHint: null,
    })
  },
  close: () =>
    set({
      isOpen: false,
      mode: "choose",
      createError: null,
      signInError: null,
      signInHint: null,
    }),
  setMode: (mode) => set({ mode }),
  clearCreateError: () => set({ createError: null }),
  clearSignInError: () => set({ signInError: null, signInHint: null }),

  createNew: async (passkeyName) => {
    // Close before the ceremony so its popup isn't hidden by our modal; reopen on failure.
    set({ isOpen: false, isCreating: true, createError: null })
    startAuthJourney({
      method: "passkey",
      action: "create",
      chain: "webauthn",
    })
    try {
      await useWebAuthnStore.getState().createNew(passkeyName)
      useLastConnectedWalletStore
        .getState()
        .recordLastConnectedWallet({ chain: "webauthn", walletId: "passkey" })
    } catch (error) {
      const errorMessage = toError(error).message
      const failureType = classifyPasskeyLoginFailure(error)
      failAuthJourney("webauthn", {
        failureStage: "ceremony",
        failureReason: failureType,
      })
      set({ isOpen: true, mode: "create", createError: errorMessage })
      if (isActionablePasskeyFailure(failureType)) {
        logger.error("Passkey creation failed", {
          error,
          failure_type: failureType,
        })
      }
    } finally {
      set({ isCreating: false })
    }
  },

  signIn: async () => {
    set({
      isOpen: false,
      isSigningIn: true,
      signInError: null,
      signInHint: null,
    })
    startAuthJourney({
      method: "passkey",
      action: "sign_in",
      chain: "webauthn",
    })
    try {
      await useWebAuthnStore.getState().signIn()
      useLastConnectedWalletStore
        .getState()
        .recordLastConnectedWallet({ chain: "webauthn", walletId: "passkey" })
    } catch (error) {
      const rawErrorMessage = toError(error).message
      const failureType = classifyPasskeyLoginFailure(error)
      failAuthJourney("webauthn", {
        failureStage: "ceremony",
        failureReason: failureType,
      })
      set({
        isOpen: true,
        mode: "choose",
        signInError: getPasskeyLoginErrorMessage(failureType, rawErrorMessage),
        signInHint: failureType === "cancelled" ? SIGN_IN_CANCELLED_HINT : null,
      })
      if (isActionablePasskeyFailure(failureType)) {
        logger.error("Passkey login failed", {
          error,
          failure_type: failureType,
        })
      }
    } finally {
      set({ isSigningIn: false })
    }
  },
}))
